import { useMemo, useState } from 'react';
import { episodeCategories, episodeList, type EpisodeItem } from '@/mocks/homeData';

const statusStyle: Record<EpisodeItem['status'], string> = {
  已校对: 'bg-secondary-100 text-secondary-900',
  译制中: 'bg-accent-100 text-accent-900',
  待校对: 'bg-primary-100 text-primary-800',
};

export default function EpisodeList() {
  const [category, setCategory] = useState<(typeof episodeCategories)[number]>('全部');

  const filtered = useMemo(
    () => (category === '全部' ? episodeList : episodeList.filter((item) => item.category === category)),
    [category],
  );

  return (
    <section id="episodes" className="w-full scroll-mt-28 bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="font-label text-xs font-medium uppercase tracking-widest text-primary-600">
              Episodes
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              分集字幕：每个章节的状态一目了然
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              每条记录包含章节简介、时长、译制来源与校对状态。用下面的分类快速筛选正片、特辑与花絮，
              先看已校对章节体验更顺畅。
            </p>
          </div>

          <div className="inline-flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {episodeCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                aria-pressed={category === item}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                  category === item
                    ? 'bg-secondary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="mt-10 rounded-lg border border-background-200 bg-background-50 px-5 py-8 text-center text-sm text-foreground-500">
            该分类下暂时没有条目，试试切换到「全部」。
          </p>
        ) : (
          <ul className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {filtered.map((item) => (
              <li
                key={item.id}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 font-label text-xs font-medium ${statusStyle[item.status]}`}>
                    {item.status}
                  </span>
                  <span className="rounded-full bg-background-100 px-2.5 py-1 font-label text-xs text-foreground-600">
                    {item.category}
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-time-line text-sm" aria-hidden="true"></i>
                    {item.runtime}
                  </span>
                </div>

                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950 md:text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{item.synopsis}</p>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-translate-2 text-sm text-secondary-600" aria-hidden="true"></i>
                    {item.translator}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
                    更新于 {item.updated}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}